import React, {useEffect, useRef, useState} from 'react';
import {
    ClientReportInfo,
    ClientReportReportStatusMap,
    ClientReportReportTypeMap,
    isShortAddendumReport,
    requireServiceType,
    shouldShowLastDayOfService,
    shouldShowNewAuthStartDate,
    TreatmentOfferedOptionMap,
    shouldShowFirstTreatmentOfferedDate,
    shouldShowTreatmentOffered,
    isClientReportStatusInReview,
    isClientReportStatusInAuthorized
} from "./domain";
import {TextFieldInput} from "../../common/component/TextFieldInput";
import {LoadingSpinner, SimpleDropdown} from "common";
import {FatOutlineButton} from "../../common/component/FatOutlineButton";
import {ClientReportFileTable} from "./ClientReportFileTable";
import {fullName} from "../../core/domain";
import {TextFieldReadonly} from "../../common/component/TextFieldReadonly";
import {formatDateStr, formatDateWithValidate} from "../../common/util/date";
import {PortalEnumNameToValue} from "../../placement/const";
import {ServiceRecommendedOptions} from "./form-components/auth-hours/domain";
import {useDeleteReportFile} from "./client-report/useDeleteReport";
import {useSyncClientReportsByOperationCase} from "./client-report/useSyncClientReportsByOperationCase";
import { useCurrentUser } from 'security';
import {showToast} from "common";
import {getDomainGraphqlError} from "graphqlsupport";
import { ProviderPortalFeatureFlag, useFeatureFlag } from 'featureflag';

type ClientReportViewModalProps = {
    report: ClientReportInfo | null;
    showModal: boolean;
    readonly?: boolean;
    hideModalFunc: () => void;
    afterRemovalFunc?: () => void;
    refetch?: () => Promise<any>;
}

const getDeleteDisabledReason = (currentUser: any, report: ClientReportInfo): string | null => {
    const canManageReport = currentUser?.canAccessClientReport;
    const isUserAdmin = currentUser?.canManageProviderUser;
    const isReportInAuthorized = isClientReportStatusInAuthorized(report?.reportStatus);

    if (!report?.sfOperationCaseId) {
        return "This report cannot be deleted because it is not linked to any Salesforce operation case yet";
    }
    if (!canManageReport) {
        return "You do not have permission to delete reports";
    }
    if (isReportInAuthorized && !isUserAdmin) {
        return "This report has already been authorized and only an admin can delete it";
    }
    return null;
};

export function ClientReportViewModal({
                                          showModal,
                                          report,
                                          hideModalFunc,
                                          afterRemovalFunc,
                                          readonly = false,
                                          refetch
                                      }: ClientReportViewModalProps) {

    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncFailed, setSyncFailed] = useState(false);
    const [pendingDeleteFile, setPendingDeleteFile] = useState<{ guid: string; fileName: string } | null>(null);
    const isRemoveReportFileDisabled = useFeatureFlag(ProviderPortalFeatureFlag.BHPNPP_2410_IS_REMOVE_REPORT_FILE_DISABLED);
    const lastSyncedReportGuid = useRef<string | null>(null);
    const currentUser = useCurrentUser();
    const {
        execute: deleteReportFile
    } = useDeleteReportFile(() => {
        if (afterRemovalFunc) {
            afterRemovalFunc();
        }
    });
    const {
        execute: syncClientReport
    } = useSyncClientReportsByOperationCase();
    const clientReportFiles = report?.clientReportFiles ?? [];
    const linkedClientReportFiles = report?.clientReportFilesBySfOperationCase?.filter(f => !clientReportFiles.some(c => c.guid === f.guid)) ?? [];

    useEffect(() => {
        const sfOperationCaseId = report?.sfOperationCaseId;
        if (!showModal) {
            lastSyncedReportGuid.current = null;
            setSyncFailed(false);
            return;
        }
        if (!sfOperationCaseId || !report?.guid || lastSyncedReportGuid.current === report.guid) {
            return;
        }

        const sync = async () => {
            setSyncing(true);
            setSyncFailed(false);
            try {
                await syncClientReport({
                    variables: {
                        sfOperationCaseId
                    }
                });
                if (refetch) {
                    await refetch();
                }
                lastSyncedReportGuid.current = report.guid;
            } catch (e: any) {
                setSyncFailed(true);
                showToast.error(getDomainGraphqlError(e) ?? 'Unable to sync report status. Please try again.');
            } finally {
                setSyncing(false);
            }
        };

        sync();
    }, [showModal, report?.guid, report?.sfOperationCaseId, refetch, syncClientReport]);

    return (
        <>
        {pendingDeleteFile !== null && (
            <div className="fixed inset-0 z-60 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">Delete File</h3>
                    <p className="text-sm text-gray-600 mb-6">Are you sure you want to delete this file? This action cannot be undone.</p>
                    <div className="flex justify-end gap-3">
                        <button
                            className="px-4 py-2 text-sm rounded border focus:outline-none"
                            disabled={loading || syncing}
                            onClick={() => setPendingDeleteFile(null)}
                            style={{ backgroundColor: '#FFF', color: '#4F6478', borderColor: '#4F6478' }}
                        >
                            Cancel
                        </button>
                        <button
                            className={`px-4 py-2 text-sm rounded text-white focus:outline-none ${loading || syncing ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={loading || syncing}
                            onClick={async () => {
                                if (!pendingDeleteFile) return;
                                setLoading(true);
                                await deleteReportFile(pendingDeleteFile.guid, pendingDeleteFile.fileName);
                                setLoading(false);
                                setPendingDeleteFile(null);
                            }}
                            style={{ backgroundColor: '#ef4444' }}
                        >
                            {loading ? 'Deleting...' : 'Yes, Delete'}
                        </button>
                    </div>
                </div>
            </div>
        )}
        <div
            className={(showModal ? "flex " : "hidden ") + "overflow-y-auto overflow-x-hidden fixed z-50 justify-center items-start inset-0"}
            style={{backgroundColor: "rgba(0,0,0,0.4)"}}>
            {/* BHPNPP-3072: fixed (viewport) overlay + flex centring, so the viewer height no longer depends on the
                Client Details top bar (BHPNPP-3030) and a tall report scrolls from the top instead of clipping. */}
            <div className="flex items-center justify-center min-h-full w-full px-4 py-6">
                <div className="relative w-full max-w-4xl">
                <div className="relative bg-white rounded-lg shadow dark:bg-gray-700 max-h-[90vh] overflow-y-auto">
                    <div className="flex justify-between items-start p-4 rounded-t border-b dark:border-gray-600">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                            Client Report
                        </h3>
                        <button type="button"
                                disabled={loading || syncing}
                                className={`text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white disabled:opacity-50 ${loading || syncing ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                onClick={() => hideModalFunc()}
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd"
                                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                      clipRule="evenodd"/>
                            </svg>
                        </button>
                    </div>
                    {
                        (loading || syncing) && <LoadingSpinner  height={"300px"} text={syncing ? "Syncing" : "Deleting"}/>
                    }
                    {
                     !(loading || syncing)  && <>
                        <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-2 justify-around w-full p-4 scrollbar-always"
                             style={{
                                 maxHeight: "300px"
                             }}
                        >
                            {
                                readonly &&
                                <>
                                    <TextFieldReadonly label={"Authorization Number"}
                                                       small
                                                       value={report?.authorization?.bhpnAuthNumber ?? ""}/>
                                    <TextFieldReadonly label={"Report Type"}
                                                       small
                                                       value={report?.reportType ? ClientReportReportTypeMap.get(report?.reportType)! : ""}/>
                                    <TextFieldReadonly label={"Report Due Date"}
                                                       small
                                                       value={report?.authorization?.reportDueDate ? formatDateStr(report.authorization?.reportDueDate) : ""}/>
                                    <TextFieldReadonly label={"Report Status"}
                                                       small
                                                       value={report?.reportStatus ? ClientReportReportStatusMap.get(report?.reportStatus)! : ""}/>
                                    <TextFieldReadonly label={"Report Submitted"}
                                                       small
                                                       value={report?.submittedAt ? formatDateWithValidate(new Date(report?.submittedAt)) : ""}/>
                                    <TextFieldReadonly label={"Report Submitted By"}
                                                       small
                                                       value={report?.submittedBy ? fullName(report?.submittedBy) : ""}/>
                                    <TextFieldReadonly label={"Report Approved"}
                                                       small
                                                       value={""}/>
                                    <TextFieldReadonly label={"Report Approved By"}
                                                       small
                                                       value={""}/>
                                    {
                                        shouldShowNewAuthStartDate(report?.reportType) &&
                                        <TextFieldReadonly label={"New Authorization"}
                                                           small
                                                           value={report?.newAuthorizationStartDate ? formatDateWithValidate(new Date(report?.newAuthorizationStartDate)) : ""}/>
                                    }
                                    {
                                        shouldShowLastDayOfService(report?.reportType) &&
                                        <TextFieldReadonly label={"Last Day Of Service Billed"}
                                                           small
                                                           value={report?.lastDayOfServiceBilled ? formatDateWithValidate(new Date(report?.lastDayOfServiceBilled)) : ""}/>
                                    }
                                    {
                                        shouldShowFirstTreatmentOfferedDate(report?.reportType) &&
                                        <TextFieldReadonly label={"First Treatment Offered Date"}
                                                           small
                                                           value={report?.firstTreatmentOfferedDate ? formatDateWithValidate(new Date(report?.firstTreatmentOfferedDate)) : ""}/>
                                    }
                                    <TextFieldReadonly label={"Include in Report Updates"}
                                                       small
                                                       multiRow
                                                       value={report?.clientReportNotifyUsers.map(u => fullName(u)).join("; ") ?? ""}/>
                                    {
                                        clinicalRecommendationPreview(report)
                                    }
                                    {
                                        shortAddendumPreview(report)
                                    }
                                </>
                            }
                            {!readonly &&
                                <>
                                    <TextFieldInput label={"Authorization Number"}
                                                    value={report?.authorizationNumber ?? ""}/>
                                    <TextFieldInput label={"Report Type"} value={report?.reportType ?? ""}/>
                                    <TextFieldInput label={"Report Due Date"} value={report?.reportDueDay ?? ""}
                                                    isDatePicker/>
                                    <div className="flex text-black-200 pb-2 items-center text-base">
                                        <div className="w-2/6 text-right">
                                            Report Status
                                        </div>
                                        <div className="ml-4 p1">
                                            <SimpleDropdown
                                                options={ClientReportReportStatusMap}
                                                selected={report?.reportStatus ?? ""}
                                                disabled={true}
                                                setSelected={() => {
                                                }}
                                                textColor={"black-200"}
                                            />
                                        </div>
                                    </div>
                                    <TextFieldInput label={"Report Submitted"} value={report?.submittedAt ?? ""}
                                                    isDatePicker/>
                                    <TextFieldInput label={"Report Submitted By"}
                                                    value={report?.submittedBy ? fullName(report?.submittedBy) : ""}/>
                                    <TextFieldInput label={"Report Approved"} value={report?.reportApproved ?? ""}
                                                    isDatePicker/>
                                    <TextFieldInput label={"Report Approved By"}
                                                    value={report?.reportApprovedBy ?? ""}/>
                                    <TextFieldInput label={"New Authorization"} value={report?.newAuthorization ?? ""}/>
                                </>
                            }
                        </div>
                    </div>
                    <div className="p-4">
                        <h3 className="font-bold pb-1">Report Files</h3>
                        <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                            <ClientReportFileTable
                                files={clientReportFiles}
                                onDelete={readonly && report && !isRemoveReportFileDisabled
                                    ? async (fileGuid) => {
                                        const file = clientReportFiles.find(f => f.guid === fileGuid);
                                        if (file) {
                                            setPendingDeleteFile({guid: file.guid, fileName: file.fileName});
                                        }
                                    }
                                    : undefined
                                }
                                deleteDisabledReason={readonly && report ? (syncFailed ? "Unable to sync report status; file delete is unavailable" : getDeleteDisabledReason(currentUser, report)) : undefined}
                            />
                        </div>
                    </div>
                    <div className="p-4">
                        <h3 className="font-bold pb-2">Linked Reports</h3>
                        <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                            <ClientReportFileTable files={linkedClientReportFiles}/>
                        </div>
                    </div>
                    </>}
                    <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                        <div className={`flex ${readonly ? "w-2/6" : "w-3/6"}`}>
                            <FatOutlineButton color={'green'}
                                              text={'Cancel'}
                                              disabled={loading || syncing}
                                              onClick={() => hideModalFunc()}/>
                            {
                                !readonly &&
                                <>
                                    <div style={{width: 10}}/>
                                    <FatOutlineButton text={"Submit Report"} color={"green"}
                                                      onClick={() => hideModalFunc()}/>
                                </>
                            }
                        </div>
                    </div>
                </div>
                </div>
            </div>
        </div>
        </>
    );
}

const clinicalRecommendationPreview = (report: ClientReportInfo | null) => {
    if (report == null || !requireServiceType(report.reportType)) {
        return <></>
    }
    return (
        <>
            <div className="font-bold text-xs col-span-2">
                Clinical Recommendations
            </div>
            <TextFieldReadonly label={"Service Recommended"}
                               small
                               value={ServiceRecommendedOptions.get(report?.serviceRecommended) ?? ''}/>
            <TextFieldReadonly label={"Service Type"}
                               small
                               value={report?.serviceType ? report?.serviceType.map((st) => PortalEnumNameToValue.get(st)!).join(';') : ""}/>
            <div/>
            <div className="text-xs col-span-2">
                Caregiver Mediated Treatment
            </div>
            <TextFieldReadonly label={"Mid-Level Hours Min"}
                               small
                               value={numToString(report?.caregiverMidLevelHoursMin)}/>
            <TextFieldReadonly label={"Mid-Level Hours Max"}
                               small
                               value={numToString(report?.caregiverMidLevelHoursMax)}/>
            <TextFieldReadonly label={"High-Level Hours Min"}
                               small
                               value={numToString(report?.caregiverHighLevelHoursMin)}/>
            <TextFieldReadonly label={"High-Level Hours Max"}
                               small
                               value={numToString(report?.caregiverHighLevelHoursMax)}/>
            <div className="text-xs col-span-2">
                Practitioner Mediated Treatment
            </div>
            <TextFieldReadonly label={"Paraprofessional Hours Min"}
                               small
                               value={numToString(report?.practitionerParaprofessionalHoursMin)}/>
            <TextFieldReadonly label={"Paraprofessional Hours Max"}
                               small
                               value={numToString(report?.practitionerParaprofessionalHoursMax)}/>
            <TextFieldReadonly label={"Mid-Level Hours Min"}
                               small
                               value={numToString(report?.practitionerMidLevelHoursMin)}/>
            <TextFieldReadonly label={"Mid-Level Hours Max"}
                               small
                               value={numToString(report?.practitionerMidLevelHoursMax)}/>
            <TextFieldReadonly label={"High-Level Hours Min"}
                               small
                               value={numToString(report?.practitionerHighLevelHoursMin)}/>
            <TextFieldReadonly label={"High-Level Hours Max"}
                               small
                               value={numToString(report?.practitionerHighLevelHoursMax)}/>
            <TextFieldReadonly label={"Group Care Hours Min"}
                               small
                               value={numToString(report?.practitionerGroupCareHoursMin)}/>
            <TextFieldReadonly label={"Group Care Hours Max"}
                               small
                               value={numToString(report?.practitionerGroupCareHoursMax)}/>
            <div className="text-xs col-span-2">
                    <span>
                        Client Offer
                    </span>
                &nbsp;
                (hours per month)
            </div>
            { shouldShowTreatmentOffered(report?.reportType ?? "") &&
                <>
                    <TextFieldReadonly label={"Treatment Offered"}
                                       small
                                       value={TreatmentOfferedOptionMap.get(report?.treatmentOffered) ?? ""}/>
                    <div />
                </>
            }
            <TextFieldReadonly label={"Paraprofessional Hours"}
                               small
                               value={numToString(report?.offeredParaprofessionalHours)}/>
            <TextFieldReadonly label={"Mid-level Hours"}
                               small
                               value={numToString(report?.offeredMidLevelHours)}/>
            <TextFieldReadonly label={"High-level Hours"}
                               small
                               value={numToString(report?.offeredHighLevelHours)}/>
            <TextFieldReadonly label={"Group Care Hours"}
                               small
                               value={numToString(report?.offeredGroupCareHours)}/>
        </>
    )
}

const numToString = (num?: number) => {
    if (num) {
        return num.toString();
    }

    return ""
}

function shortAddendumPreview(report: ClientReportInfo | null) {
    if (report == null || !isShortAddendumReport(report.reportType)) {
        return <></>
    }
    return (
        <>
            <TextFieldReadonly label={"Name and Title"}
                               small
                               value={report.nameAndTitle}/>
            <TextFieldReadonly label={"License/Certificate #"}
                               small
                               value={report.licenseNumber}/>
            <TextFieldReadonly label={"Clinical Rationale"}
                               labelAtTop
                               small
                               fullRow
                               multiRow
                               value={report.clinicalRationale}/>
        </>
    );
}
